import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api-client';
import type {
  DeliverabilityHealthSummaryDto,
  DomainReadinessReportDto,
  IpPoolDto,
  WarmupProfileDto,
  MailboxResponse,
} from '@antigravity/types';

export const DeliverabilityPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'health' | 'warmup' | 'readiness' | 'ip-pools'>('health');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [healthSummary, setHealthSummary] = useState<DeliverabilityHealthSummaryDto | null>(null);
  const [warmupProfiles, setWarmupProfiles] = useState<WarmupProfileDto[]>([]);
  const [mailboxes, setMailboxes] = useState<MailboxResponse[]>([]);
  const [ipPools, setIpPools] = useState<IpPoolDto[]>([]);
  const [domainReadiness, setDomainReadiness] = useState<DomainReadinessReportDto | null>(null);
  const [readinessDomainId, setReadinessDomainId] = useState<string>('');

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [health, profiles, mbList, pools] = await Promise.all([
        apiClient.getDeliverabilityHealth(30).catch(() => null),
        apiClient.getWarmupProfiles().catch(() => []),
        apiClient.listMailboxes({ limit: 50 }).catch(() => ({ items: [] })),
        apiClient.getIpPools().catch(() => []),
      ]);

      if (health) setHealthSummary(health);
      setWarmupProfiles(profiles);
      const items = mbList.items || [];
      setMailboxes(items);
      setIpPools(pools);

      if (items.length > 0 && items[0].sendingDomainId) {
        setReadinessDomainId(items[0].sendingDomainId);
        apiClient
          .getDomainReadiness(items[0].sendingDomainId)
          .then(setDomainReadiness)
          .catch(() => {});
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load deliverability data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const handleStartWarmup = async (mailboxId: string) => {
    try {
      await apiClient.startWarmup(mailboxId);
      await loadAllData();
    } catch (err: any) {
      alert(`Failed to start warmup: ${err.message}`);
    }
  };

  const handlePauseWarmup = async (mailboxId: string) => {
    const reason = prompt('Reason for pausing warmup:', 'Manual pause by operator');
    if (reason === null) return;
    try {
      await apiClient.pauseWarmup(mailboxId, reason);
      await loadAllData();
    } catch (err: any) {
      alert(`Failed to pause warmup: ${err.message}`);
    }
  };

  const handleResumeWarmup = async (mailboxId: string) => {
    try {
      await apiClient.resumeWarmup(mailboxId);
      await loadAllData();
    } catch (err: any) {
      alert(`Failed to resume warmup: ${err.message}`);
    }
  };

  const handleCheckDomainReadiness = async (domainId: string) => {
    if (!domainId) return;
    try {
      setLoading(true);
      const rep = await apiClient.getDomainReadiness(domainId);
      setDomainReadiness(rep);
    } catch (err: any) {
      alert(`Readiness check failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            <span>Deliverability & Mailbox Warmup</span>
            {healthSummary && (
              <span
                className={`text-xs px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wider ${
                  healthSummary.overallStatus === 'HEALTHY'
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : healthSummary.overallStatus === 'WARNING'
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                }`}
              >
                {healthSummary.overallStatus}
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Automated mailbox warmup, reputation health telemetry, IP pools, and authentication readiness.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('health')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              activeTab === 'health'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Health & Metrics
          </button>
          <button
            onClick={() => setActiveTab('warmup')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              activeTab === 'warmup'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Warmup Engine
          </button>
          <button
            onClick={() => setActiveTab('readiness')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              activeTab === 'readiness'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Domain Readiness
          </button>
          <button
            onClick={() => setActiveTab('ip-pools')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              activeTab === 'ip-pools'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            IP Pools
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-sm text-rose-400 flex items-center gap-3">
          <span className="font-semibold">Error:</span> {error}
        </div>
      )}

      {loading && !healthSummary ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mr-3"></div>
          Analyzing deliverability infrastructure and telemetry...
        </div>
      ) : (
        <>
          {/* TAB 1: HEALTH & METRICS */}
          {activeTab === 'health' && healthSummary && (
            <div className="space-y-6">
              {/* Metric KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="text-xs font-medium text-slate-400">Delivery Rate</div>
                  <div className="text-2xl font-bold text-white mt-1">
                    {(healthSummary.deliveryRate * 100).toFixed(1)}%
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {healthSummary.totalDelivered} of {healthSummary.totalSent} delivered
                  </div>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="text-xs font-medium text-slate-400">Hard Bounce Rate</div>
                  <div
                    className={`text-2xl font-bold mt-1 ${
                      healthSummary.hardBounceRate >= 0.05
                        ? 'text-rose-400'
                        : healthSummary.hardBounceRate >= 0.02
                        ? 'text-amber-400'
                        : 'text-emerald-400'
                    }`}
                  >
                    {(healthSummary.hardBounceRate * 100).toFixed(2)}%
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Threshold limit: &lt; 5.0%</div>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="text-xs font-medium text-slate-400">Complaint Rate</div>
                  <div
                    className={`text-2xl font-bold mt-1 ${
                      healthSummary.complaintRate >= 0.001 ? 'text-rose-400' : 'text-emerald-400'
                    }`}
                  >
                    {healthSummary.complaintRateAvailable
                      ? `${(healthSummary.complaintRate * 100).toFixed(3)}%`
                      : '0.00% (No FBL)'}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {healthSummary.complaintRateAvailable
                      ? 'Measured from FBL webhooks'
                      : 'Authoritative FBL not configured'}
                  </div>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="text-xs font-medium text-slate-400">Unsubscribe Rate</div>
                  <div className="text-2xl font-bold text-white mt-1">
                    {(healthSummary.unsubscribeRate * 100).toFixed(2)}%
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Window: {healthSummary.timeWindow}</div>
                </div>
              </div>

              {/* Recommendations Box */}
              <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                  Deliverability Health Insights & Recommendations
                </h3>
                <ul className="space-y-2">
                  {healthSummary.recommendations.map((rec, i) => (
                    <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                      <span className="text-indigo-400 font-bold">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* TAB 2: WARMUP ENGINE */}
          {activeTab === 'warmup' && (
            <div className="space-y-6">
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                <h3 className="text-base font-semibold text-white mb-2">Mailbox Warmup Profiles</h3>
                <p className="text-sm text-slate-400 mb-6">
                  Automated ramp schedule gradually increases daily sending ceilings to build provider trust and avoid ISP greylisting.
                </p>

                {mailboxes.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 text-sm">
                    No mailboxes found in this organization. Create a mailbox first.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {mailboxes.map((mb) => {
                      const profile = warmupProfiles.find((p) => p.mailboxId === mb.id);
                      const isPaused = profile?.status === 'PAUSED';
                      const isActive = profile?.status === 'ACTIVE';
                      const isNotStarted = !profile || profile.status === 'NOT_STARTED';

                      const sent = profile?.dailySentCount || 0;
                      const limit = profile?.dailyTargetLimit || 50;
                      const maxVol = profile?.maxDailyVolume || 500;
                      const pct = Math.min(100, Math.round((sent / limit) * 100));

                      return (
                        <div
                          key={mb.id}
                          className="bg-slate-950/40 border border-slate-800/80 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
                        >
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-white flex items-center gap-2">
                              {mb.emailAddress}
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${
                                  isActive
                                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                    : isPaused
                                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                    : 'bg-slate-800 text-slate-400'
                                }`}
                              >
                                {profile ? profile.status : 'NOT_STARTED'}
                              </span>
                            </div>
                            <div className="text-xs text-slate-400">
                              Day {profile?.currentDay || 1} • Daily Target: {limit} • Max Target: {maxVol}
                            </div>
                            {profile?.pauseReason && (
                              <div className="text-xs text-amber-400 font-medium">
                                Paused: {profile.pauseReason}
                              </div>
                            )}

                            {/* Progress bar */}
                            <div className="w-64 bg-slate-800 rounded-full h-1.5 mt-2">
                              <div
                                className="bg-indigo-500 h-1.5 rounded-full transition-all"
                                style={{ width: `${pct}%` }}
                              ></div>
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {sent} of {limit} allowance used today ({pct}%)
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            {isNotStarted && (
                              <button
                                onClick={() => handleStartWarmup(mb.id)}
                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-lg transition"
                              >
                                Start Warmup
                              </button>
                            )}
                            {isActive && (
                              <button
                                onClick={() => handlePauseWarmup(mb.id)}
                                className="px-3 py-1.5 bg-amber-600/20 border border-amber-500/30 text-amber-400 hover:bg-amber-600/30 text-xs font-medium rounded-lg transition"
                              >
                                Pause
                              </button>
                            )}
                            {isPaused && (
                              <button
                                onClick={() => handleResumeWarmup(mb.id)}
                                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-lg transition"
                              >
                                Resume Warmup
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: DOMAIN READINESS */}
          {activeTab === 'readiness' && (
            <div className="space-y-6">
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-base font-semibold text-white">Domain Authentication & TLS Readiness</h3>
                    <p className="text-sm text-slate-400">
                      Read-only audit inspecting SPF, DKIM, DMARC, MX, PTR/rDNS and TLS validity without modifying DNS or firewall rules.
                    </p>
                  </div>

                  {mailboxes.length > 0 && (
                    <div className="flex items-center gap-2">
                      <select
                        value={readinessDomainId}
                        onChange={(e) => {
                          setReadinessDomainId(e.target.value);
                          handleCheckDomainReadiness(e.target.value);
                        }}
                        className="bg-slate-950 border border-slate-700 text-white text-xs rounded-lg px-3 py-2"
                      >
                        {mailboxes
                          .filter((m) => m.sendingDomainId)
                          .map((m) => (
                            <option key={m.id} value={m.sendingDomainId!}>
                              {m.emailAddress.split('@')[1]}
                            </option>
                          ))}
                      </select>
                      <button
                        onClick={() => handleCheckDomainReadiness(readinessDomainId)}
                        className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-medium rounded-lg transition"
                      >
                        Re-evaluate
                      </button>
                    </div>
                  )}
                </div>

                {domainReadiness ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between bg-slate-950/60 border border-slate-800 p-4 rounded-xl">
                      <div>
                        <div className="text-sm font-semibold text-white">{domainReadiness.domain}</div>
                        <div className="text-xs text-slate-400">Mail Host: {domainReadiness.mailHost}</div>
                      </div>
                      <span
                        className={`text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider ${
                          domainReadiness.overallStatus === 'PASS'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                        }`}
                      >
                        {domainReadiness.overallStatus}
                      </span>
                    </div>

                    {domainReadiness.operatorBlockers.length > 0 && (
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                        <div className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2">
                          Operator Remediation Required
                        </div>
                        <ul className="space-y-1">
                          {domainReadiness.operatorBlockers.map((b, i) => (
                            <li key={i} className="text-xs text-slate-300 flex items-center gap-2">
                              <span className="text-amber-400 font-bold">•</span>
                              {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Detailed Checks Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs text-slate-300">
                        <thead className="bg-slate-950 text-slate-400 uppercase font-semibold border-b border-slate-800">
                          <tr>
                            <th className="px-4 py-3">Check</th>
                            <th className="px-4 py-3">Type</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Details</th>
                            <th className="px-4 py-3">Remediation</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                          {domainReadiness.checks.map((chk, i) => (
                            <tr key={i} className="hover:bg-slate-900/40">
                              <td className="px-4 py-3 font-medium text-white">{chk.name}</td>
                              <td className="px-4 py-3 font-mono text-slate-400">{chk.type}</td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-0.5 rounded font-semibold ${
                                    chk.status === 'PASS'
                                      ? 'text-emerald-400 bg-emerald-500/10'
                                      : chk.status === 'FAIL'
                                      ? 'text-rose-400 bg-rose-500/10'
                                      : 'text-slate-400 bg-slate-800'
                                  }`}
                                >
                                  {chk.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 max-w-xs truncate text-slate-400" title={chk.message}>
                                {chk.message}
                              </td>
                              <td className="px-4 py-3 max-w-xs text-slate-400">
                                {chk.remediation || 'None'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500 text-sm">
                    Select a domain to inspect readiness evidence.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: IP POOLS */}
          {activeTab === 'ip-pools' && (
            <div className="space-y-6">
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                <h3 className="text-base font-semibold text-white mb-2">Dedicated & Shared IP Pools</h3>
                <p className="text-sm text-slate-400 mb-6">
                  Manage sending IP pools. Dedicated IPs are assigned to enterprise tenants with custom reverse DNS alignment.
                </p>

                <div className="space-y-4">
                  {ipPools.map((pool) => (
                    <div
                      key={pool.id}
                      className="bg-slate-950/40 border border-slate-800 rounded-xl p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-white flex items-center gap-2">
                            {pool.name}
                            <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-slate-800 text-slate-300">
                              {pool.isShared ? 'SHARED POOL' : 'DEDICATED TENANT POOL'}
                            </span>
                          </div>
                          {pool.description && (
                            <div className="text-xs text-slate-400 mt-0.5">{pool.description}</div>
                          )}
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          {pool.status}
                        </span>
                      </div>

                      {/* IP List */}
                      <div className="border-t border-slate-800/80 pt-3">
                        <div className="text-xs font-medium text-slate-400 mb-2">Assigned IP Addresses:</div>
                        {pool.ips && pool.ips.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {pool.ips.map((ip) => (
                              <div
                                key={ip.id}
                                className="bg-slate-900 border border-slate-800 rounded-lg p-2.5 flex items-center justify-between text-xs"
                              >
                                <div>
                                  <div className="font-mono font-bold text-white">{ip.ipAddress}</div>
                                  <div className="text-slate-400 text-[10px]">
                                    rDNS: {ip.reverseDns || 'Not configured'} • Cap: {ip.maxDailyCapacity}/day
                                  </div>
                                </div>
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-amber-500/10 text-amber-400">
                                  {ip.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500 italic">No IPs assigned to this pool.</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
